import { useMemo } from 'react';
import { usePayments } from './usePayments';
import { useSession } from '../session';
import { useAuth } from '../auth/useAuth';

/** Voluntary top-up has no pack price to anchor on, unlike the
 *  `INSUFFICIENT_BALANCE` flow — $10 is a sane starting amount, editable
 *  on the deposit screen's own keypad. */
const VOLUNTARY_TOP_UP_CENTS = 1000;

/**
 * The balance-pill tap handler for a *voluntary* top-up: opens the Stripe
 * deposit screen (`withdrawable: true` surfaces the Withdraw link, unlike the
 * reactive INSUFFICIENT_BALANCE top-up during a purchase).
 *
 * Returns `undefined` for seamless operators — they have no deposit flow, so
 * the caller passes no `onClick` and `BalancePill` stays a read-only display.
 * Stripe operators get an interactive pill.
 *
 * While anonymous (embed mode, no session token) the deposit endpoints have no
 * public twin, so the sign-in modal opens first and the top-up only proceeds
 * once a session exists.
 */
export function useVoluntaryTopUp(): (() => void) | undefined {
  const { isStripeFunded, requestTopUp } = usePayments();
  const { requestAuth } = useAuth();
  const session = useSession();
  const getToken = session.getToken;

  return useMemo(() => {
    if (!isStripeFunded) return undefined;
    return () => {
      void (async () => {
        if (!getToken()) {
          const ok = await requestAuth();
          if (!ok) return;
        }
        void requestTopUp(VOLUNTARY_TOP_UP_CENTS, { withdrawable: true });
      })();
    };
  }, [isStripeFunded, requestTopUp, requestAuth, getToken]);
}
